import { IReportEntry, Kpi, KpiGrid } from '@localina/core';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface IReportKpisProps {
    data: IReportEntry[];

    previousData?: IReportEntry[];
}

interface ITotals {
    valid: number;
    noShow: number;
}

const getTotals = (entries: IReportEntry[]): ITotals =>
    entries.reduce<ITotals>(
        (acc, entry) => ({
            valid: acc.valid + entry.countWalkins + entry.countManual + entry.countOnline,
            noShow: acc.noShow + entry.countNoShow,
        }),
        { valid: 0, noShow: 0 },
    );

const getPercentChange = (current: number, previous: number): number | undefined => {
    if (previous === 0) {
        return undefined;
    }
    return ((current - previous) / previous) * 100;
};

const formatPercent = (value: number, fractionDigits = 1) =>
    `${value.toLocaleString(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    })}%`;

export const ReportKpis: React.FC<IReportKpisProps> = (props) => {
    const { t } = useTranslation();

    const totals = getTotals(props.data);
    const previousTotals = props.previousData ? getTotals(props.previousData) : undefined;

    const change = previousTotals ? getPercentChange(totals.valid, previousTotals.valid) : undefined;
    const noShowRate = totals.valid > 0 ? (totals.noShow / totals.valid) * 100 : 0;

    const changeHint = () => {
        if (change === undefined) {
            return undefined;
        }
        if (Math.round(change) === 0) {
            return t('reports.kpi.unchanged');
        }
        const arrow = change > 0 ? '▲' : '▼';
        return `${arrow} ${formatPercent(Math.abs(change), 0)} ${t('reports.kpi.vsPrevious')}`;
    };

    const changeTone = change === undefined || Math.round(change) === 0 ? 'neutral' : change > 0 ? 'up' : 'down';

    return (
        <KpiGrid columns={2}>
            <Kpi
                label={t('reports.kpi.reservations')}
                value={totals.valid.toLocaleString()}
                hint={changeHint()}
                tone={changeTone}
            />
            <Kpi
                label={t('reports.kpi.noShows')}
                value={totals.noShow.toLocaleString()}
                hint={t('reports.kpi.noShowRate', { percent: formatPercent(noShowRate) })}
                tone={totals.noShow > 0 ? 'down' : 'neutral'}
            />
        </KpiGrid>
    );
};
